import axios from '../../shared/api/axios';

class AuthService {
    constructor() {
        this.tokenKey = 'accessToken';
        this.userKey = 'userData';
        this.expiredAtKey = 'expiredAt';
        this.refreshInterval = null;
        this.refreshIntervalTime = 30 * 60 * 1000; // 30 minutes
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.isLoggedOut = false;
    }

    // Lưu thông tin đăng nhập
    setAuthData(authData) {
        console.log('AuthService.setAuthData called with:', authData);
        const { accessToken, userData, expiredAt } = authData;
        
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.userKey, JSON.stringify(userData));
        localStorage.setItem(this.expiredAtKey, expiredAt);
        
        // Reset logout flag
        this.isLoggedOut = false;
        
        console.log('Auth data saved to localStorage');
        
        // Thiết lập interceptor cho axios
        this.setupAxiosInterceptor();
        
        // Bắt đầu refresh token tự động
        this.startTokenRefresh();
    }

    // Lấy access token
    getAccessToken() {
        return localStorage.getItem(this.tokenKey);
    }

    // Lấy user data
    getUserData() {
        const userData = localStorage.getItem(this.userKey);
        return userData ? JSON.parse(userData) : null;
    }

    // Lấy thời gian hết hạn
    getExpiredAt() {
        return localStorage.getItem(this.expiredAtKey);
    }

    // Kiểm tra đã đăng nhập chưa
    isAuthenticated() {
        const token = this.getAccessToken();
        const expiredAt = this.getExpiredAt();
        
        console.log('AuthService.isAuthenticated - token:', token ? 'exists' : 'null');
        console.log('AuthService.isAuthenticated - expiredAt:', expiredAt);
        
        if (!token || !expiredAt) {
            console.log('AuthService.isAuthenticated - missing token or expiredAt');
            return false;
        }

        // Kiểm tra token có hết hạn không
        const now = Math.floor(Date.now() / 1000); // Current timestamp in seconds
        const expiredTime = parseInt(expiredAt); // expiredAt is already a timestamp
        
        console.log('AuthService.isAuthenticated - now:', now, 'expiredTime:', expiredTime);
        const isAuth = now < expiredTime;
        console.log('AuthService.isAuthenticated - result:', isAuth);
        
        return isAuth;
    }

    // Đăng xuất
    logout() {
        // Set logout flag để ngăn chặn refresh token
        this.isLoggedOut = true;
        
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.userKey);
        localStorage.removeItem(this.expiredAtKey);
        
        // Reset refresh flags
        this.isRefreshing = false;
        this.refreshPromise = null;
        
        // Dừng refresh token
        this.stopTokenRefresh();
        
        // Xóa interceptor
        this.removeAxiosInterceptor();
    }

    // Thiết lập axios interceptor để tự động thêm token vào header
    setupAxiosInterceptor() {
        this.removeAxiosInterceptor(); // Xóa interceptor cũ nếu có
        
        this.requestInterceptor = axios.interceptors.request.use(
            (config) => {
                const token = this.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        this.responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                
                // Kiểm tra xem có phải là refresh token request không
                const isRefreshRequest = originalRequest.url?.includes('auth/refresh');
                
                // Nếu lỗi 401 và chưa retry và không phải refresh request
                if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
                    originalRequest._retry = true;
                    
                    try {
                        // Thử refresh token
                        const newToken = await this.refreshToken();
                        if (newToken) {
                            // Retry request với token mới
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return axios(originalRequest);
                        }
                    } catch (refreshError) {
                        console.error('Refresh token failed:', refreshError);
                        // Refresh token thất bại, đăng xuất
                        this.logout();
                        window.location.href = '/auth/login';
                        return Promise.reject(refreshError);
                    }
                }
                
                // Nếu là refresh request và bị 401, đăng xuất ngay
                if (isRefreshRequest && error.response?.status === 401) {
                    console.error('Refresh token endpoint returned 401, logging out');
                    this.logout();
                    window.location.href = '/auth/login';
                }
                
                return Promise.reject(error);
            }
        );
    }

    // Xóa axios interceptor
    removeAxiosInterceptor() {
        if (this.requestInterceptor) {
            axios.interceptors.request.eject(this.requestInterceptor);
            this.requestInterceptor = null;
        }
        
        if (this.responseInterceptor) {
            axios.interceptors.response.eject(this.responseInterceptor);
            this.responseInterceptor = null;
        }
    }

    // Refresh token
    async refreshToken() {
        // Kiểm tra xem đã logout chưa
        if (this.isLoggedOut) {
            console.log('User is logged out, skipping token refresh');
            throw new Error('User is logged out');
        }
        
        // Nếu đang refresh, trả về promise hiện tại
        if (this.isRefreshing) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = this._performRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    // Thực hiện refresh token
    async _performRefresh() {
        try {
            const currentToken = this.getAccessToken();
            if (!currentToken) {
                throw new Error('No token to refresh');
            }

            console.log('Attempting to refresh token...');
            
            const response = await axios.post('auth/refresh', {
                accessToken: currentToken
            }, {
                headers: {
                    'Authorization': `Bearer ${currentToken}`
                }
            });

            const { data } = response.data;
            
            // Cập nhật token mới
            localStorage.setItem(this.tokenKey, data.accessToken);
            localStorage.setItem(this.expiredAtKey, data.expiredAt);
            
            console.log('Token refreshed successfully');
            return data.accessToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            throw error;
        }
    }

    // Bắt đầu refresh token tự động
    startTokenRefresh() {
        this.stopTokenRefresh(); // Dừng interval cũ nếu có
        
        this.refreshInterval = setInterval(async () => {
            try {
                // Kiểm tra xem đã logout chưa
                if (this.isLoggedOut) {
                    console.log('User is logged out, stopping auto refresh');
                    this.stopTokenRefresh();
                    return;
                }
                
                // Kiểm tra xem có đang refresh không
                if (this.isRefreshing) {
                    console.log('Already refreshing token, skipping...');
                    return;
                }
                
                if (this.isAuthenticated()) {
                    await this.refreshToken();
                } else {
                    console.log('Token expired, stopping auto refresh');
                    this.stopTokenRefresh();
                }
            } catch (error) {
                console.error('Auto token refresh failed:', error);
                this.stopTokenRefresh();
            }
        }, this.refreshIntervalTime);
    }

    // Dừng refresh token tự động
    stopTokenRefresh() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    // Khởi tạo service (gọi khi app start)
    init() {
        if (this.isAuthenticated()) {
            this.setupAxiosInterceptor();
            this.startTokenRefresh();
        }
    }
}

// Tạo instance duy nhất
const authService = new AuthService();

export default authService;
